import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import AppLink from '../../../components/links/AppLink';
import { Image, SvgIcon } from 'edcom-ui';
import styles from './SubNavDropdown.module.less';
import { tabInfoPropType, isElementVisible } from '../util';
import { trackGA } from 'services/track';

const SubNavDropdown = ({ tabInfo, closeDropdown, buttonRefs }) => {
  const { name, urlName, gradesForContentType, sections, seeAll } = tabInfo;
  const [fullOpacity, setFullOpacity] = useState(false);
  const dropdownRef = useRef(null);

  // DOM element of the SubNavButton associated with this dropdown. Necessary for accessibility tabbing.
  const buttonElem = buttonRefs[urlName] ? buttonRefs[urlName].current : null;

  const handleDropdownClick = (sectionName, text, url) => {
    closeDropdown();
    const gtmObject = {
      event: 'click_button',
      feature: 'Navigation',
      section: `${name} Sub-Menu`,
      button: `${sectionName} Links`,
      click_text: text,
      click_url: `https://${window.location.hostname}${url}`,
    };
    trackGA('Sub Menu', 'click_button', '', '', gtmObject);
  };

  useEffect(() => {
    // Trigger opacity animation. Seen when you go directly from one open dropdown button to another
    setFullOpacity(true);

    // TABBING (ACCESSIBILITY) SETUP:
    const dropdownToggle = buttonElem.querySelector('button');
    // Used to clear the event listener of the dropdownToggle button when we unmount the component
    let clearDropdownToggleListener = () => {};

    if (dropdownRef.current && dropdownRef.current.querySelector('a')) {
      // All visible link in the dropdown
      const linksInDropdown = Array.from(
        dropdownRef.current.querySelectorAll('a:not([hidden])')
      ).filter(link => isElementVisible(link));

      if (linksInDropdown.length >= 1) {
        const selectFirstOnDropdownTab = event => {
          if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            linksInDropdown[0].focus();
          }
        };

        dropdownToggle.addEventListener('keydown', selectFirstOnDropdownTab);
        clearDropdownToggleListener = () => {
          dropdownToggle.removeEventListener(
            'keydown',
            selectFirstOnDropdownTab
          );
        };

        // Close the dropdown tabs away from the last link on the list
        linksInDropdown[linksInDropdown.length - 1].onkeydown = e => {
          // Only continue if Tab is pressed (not Shift+Tab)
          if (e.key !== 'Tab' || e.shiftKey) return;

          e.preventDefault();
          if (
            buttonElem &&
            buttonElem.nextSibling &&
            buttonElem.nextSibling.querySelector('a')
          ) {
            // Focus on the next element
            buttonElem.nextSibling.querySelector('a').focus();
          }
          closeDropdown(false);
        };

        // Close the dropdown if the user presses Shift+Tab, the move focus back to the "open dropdown" toggle button
        linksInDropdown[0].onkeydown = e => {
          if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            closeDropdown(false);
            dropdownToggle.focus();
          }
        };
      }
    }

    // While the dropdown is open, close it if the Escape key is pressed
    const closeDropdownOnEsc = event => {
      if (event.key !== 'Escape') return;
      closeDropdown(false);
      dropdownToggle.focus();
    };
    window.addEventListener('keyup', closeDropdownOnEsc);
    return () => {
      window.removeEventListener('keyup', closeDropdownOnEsc);
      clearDropdownToggleListener();
    };
  }, []);

  const buildDropdownColumns = (rows, sectionName, seeAllLink = false) => {
    const columns = [];
    let columnNumber = -1;
    let addMarginToItem;
    let lastItemIsChildren = false;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const isEmptySpace = row.type === 'Empty Space';
      const isMainItem = row.type === 'Main Item';

      addMarginToItem = isMainItem;
      if (row.column > columnNumber) {
        columnNumber = row.column;
        columns.push([]);
        addMarginToItem = false;
      }

      columns[columns.length - 1].push(
        <li
          key={row.title.toLowerCase().replace(/\s/g, '')}
          className={clsx(
            isMainItem && styles.mainItem,
            isMainItem &&
              addMarginToItem &&
              !lastItemIsChildren &&
              styles.mainItemTopMargin,
            isMainItem && !addMarginToItem && styles.mainItemNoTopMargin,
            !isMainItem && styles.childItem,
            row.button && styles.hiddenButton // For more info, see addButtonsToMoreTab() in SubNav/index.js
          )}
        >
          {isEmptySpace ? (
            // Render a div if it's an empty space
            <div className={styles.emptySpace}></div>
          ) : (
            // Render AppLink if it's not an empty space
            <AppLink
              href={row.url}
              onClick={() =>
                handleDropdownClick(sectionName, row.title, row.url)
              }
            >
              {row.icon !== null && row.icon !== '' && (
                <Image className={styles.icon} src={row.icon} alt="" />
              )}
              <div className={styles.childItem}>{row.title}</div>
            </AppLink>
          )}
        </li>
      );
      lastItemIsChildren = !isMainItem;
    }

    if (seeAllLink) {
      let startIndex = 0;
      if (columns.length === 0) {
        columns.push([]);
      } else {
        startIndex = columns.length - 1;
      }
      columns[startIndex].push(
        <li
          key="seeAllLink"
          className={clsx(
            styles.seeAllLink,
            columns[0].length === 0 && styles.seeAllLinkOnTop
          )}
        >
          <AppLink
            href={seeAll.url}
            onClick={() =>
              handleDropdownClick('See All', seeAll.title, seeAll.url)
            }
          >
            {seeAll.title}
            <SvgIcon name="arrowRight" aria-hidden width={16} height={16} />
          </AppLink>
        </li>
      );
    }

    return columns.map((column, i) => (
      <ul key={`column${i}`} className={styles.column}>
        {column}
      </ul>
    ));
  };

  const buildDropDown = sections => {
    const generatedSections = [];

    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const { title, items } = section;

      generatedSections.push(
        <Fragment key={`section-container-${title}`}>
          {!(name === 'More' && i === 0) && (
            <div className={styles.spacer} key={`spacer-${i}`}>
              <div className={styles.headerDivider} />
            </div>
          )}
          <div className={styles.section} key={`section-${i}`}>
            <h6 className={styles.labelFont}>{title}</h6>
            <div className={styles.subNavColumns}>
              {title === 'Additional Topics' && Object.keys(seeAll).length !== 0
                ? buildDropdownColumns(items, title, true)
                : buildDropdownColumns(items, title)}
            </div>
          </div>
        </Fragment>
      );
    }
    return <>{generatedSections}</>;
  };

  return (
    <div className={styles.subNavDropdown} ref={dropdownRef}>
      <div
        className={clsx(
          styles.container,
          styles.gradesMargin,
          fullOpacity && styles.open
        )}
      >
        {name !== 'More' && (
          <div className={styles.section}>
            <h6 className={styles.labelFont}>By Grade</h6>
            <div className={styles.subNavColumns}>
              <ul className={styles.column}>
                {gradesForContentType !== null &&
                  gradesForContentType.length > 0 &&
                  gradesForContentType.map((grade, index) => (
                    <li
                      className={clsx(
                        styles.mainItem,
                        index !== 0 && styles.mainItemTopMargin,
                        index === 0 && styles.mainItemNoTopMargin
                      )}
                      key={grade.urlName}
                    >
                      <AppLink
                        href={`${seeAll.url}${grade.urlName}/`}
                        onClick={() =>
                          handleDropdownClick(
                            'By Grade',
                            grade.longDisplay,
                            `${seeAll.url}${grade.urlName}/`
                          )
                        }
                      >
                        {grade.longDisplay}
                      </AppLink>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        )}
        <>{buildDropDown(sections)}</>
      </div>
    </div>
  );
};

SubNavDropdown.propTypes = {
  tabInfo: tabInfoPropType.isRequired,
  closeDropdown: PropTypes.func.isRequired,
  buttonRefs: PropTypes.shape({
    urlName: PropTypes.shape({ current: PropTypes.object }),
  }),
};

SubNavDropdown.defaultProps = {
  buttonRefs: null,
};

export default SubNavDropdown;
